import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MessageService } from 'primeng/api';
import { InputNumber } from 'primeng/inputnumber';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, interval } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

import { ItemsService } from '../../services/financeiro/item.service';
import { GrupoService } from '../../services/grupo/grupo.service';
import { PedidoService } from '../../services/pedidos/pedido.service';
import { EmpresaConfigService } from '../../services/configuracoes/empresa.service';
import { CidadeService } from '../../services/configuracoes/cidade.service';
import { ContaService } from '../../services/conta/conta.service';
import { IntencaoDeRecebimentoService } from '../../services/financeiro/intencao-de-recebimento.service';
import { EstadoDePedido } from '../../enums/EstadoDePedido.enum';
import { EntitySelectorConfig } from '../../shared/components/entity-selector/entity-selector.interfaces';
import { PessoaDTO } from '../../models/clientes/pessoa.model';
import { environment } from '../../environments/environment';
import { toLocalISOString } from '../../shared/utils/date-utils';
import { PermissaoService } from '../../services/permissao/permissao.service';
import { PersonalizacaoDeItemSelecionada } from '../../models/produto-variante-modificador/produto-variante-modificador.model';
import {
  VarianteDePrecoDeItemPedidoDTO,
  ModificadorDePrecoDeItemPedidoDTO,
} from '../../models/pedidos/pedido.model';
import { corrigirIconesLeaflet } from '../../shared/utils/leaflet-icons.util';

/**
 * Linha do carrinho do pedido (editável na tabela do painel direito).
 *
 * Itens SEM personalização somam quantidade quando o mesmo produto é adicionado
 * de novo. Itens COM personalização (variante e/ou modificadores) nunca somam:
 * cada seleção vira uma linha própria. Os arrays de variante/modificador ficam
 * no formato "por unidade" (variante com quantidade 1, modificador com a
 * quantidade de adicionais escolhidos); a multiplicação pela quantidade da linha
 * acontece no envio (`criarPedido`), igual ao PDV.
 */
/** O que aparece embaixo do nome do item: nada, o codigo interno ou o de barras. */
type ExibicaoCodigo = 'NENHUM' | 'CODIGO' | 'BARRAS';

interface LinhaPedido {
  itemId: number;
  nome: string;
  codigoItem?: string;
  barras?: string;
  valorUnitario: number;
  quantidade: number;
  saldo: number;
  // Só mercadoria limita a quantidade ao saldo; serviço não controla estoque
  // (mesma regra da venda).
  controlaSaldo: boolean;
  // Personalização (opcional) — presente apenas quando o produto tem variante/modificadores.
  variantesDePreco?: VarianteDePrecoDeItemPedidoDTO[];
  modificadores?: ModificadorDePrecoDeItemPedidoDTO[];
  varianteDescricao?: string | null;
  modificadoresDescricao?: string | null;
  // Observação derivada da personalização (variante/modificadores) escolhida no dialog.
  observacaoItem?: string;
  // Observação digitada manualmente pelo funcionário para este item (editável no carrinho).
  observacaoManual?: string;
}

/**
 * Modalidade do pedido criado pelo funcionário — paridade com o mobile.
 * Cada modalidade tem o seu tipo de entrega no backend (a mesa/comanda ainda é
 * só de experiência); `DELIVERY` vai como `DELIVERY` e habilita os campos de
 * envio (endereço, referência, ponto no mapa e valor do frete).
 */
type Modalidade = 'LOCAL' | 'RETIRADA' | 'DELIVERY';

/**
 * Tela "Criar Pedido" — cria pedidos por fora do minishop, no acesso do próprio
 * cliente. Reaproveita o layout da tela de Vendas: grid de produtos (com chips de
 * categoria, busca e "mostrar sem saldo") à esquerda e carrinho à direita — SEM as
 * partes de desconto e timbrado. Lista mercadorias e serviços com a mesma regra
 * da venda: só mercadoria exige saldo, e item com preço 0 pede o valor de venda
 * antes de entrar. Cria o pedido já em EM_PRODUCAO (tipo COMANDA).
 *
 * Paridade com o mobile: modalidade local/retirada/delivery, mesa e comanda (só
 * no local) e, para delivery, endereço + mapa + frete calculado pelo backend.
 */
/** Formas de pagamento que um pedido aceita (espelha o enum FormaDePagamentoPedido do backend). */
export type FormaDePagamentoPedido = 'DINHEIRO' | 'TARJETA' | 'QR_CODE' | 'TRANSFERENCIA';

@Component({
  selector: 'app-criar-pedido',
  templateUrl: './criar-pedido.component.html',
  styleUrls: ['./criar-pedido.component.scss'],
})
export class CriarPedidoComponent implements OnInit, OnDestroy {
  env = environment;
  idEmpresa = '';

  /** Fallback de coordenada quando não há ponto definido (Assunção, Paraguai). */
  private static readonly LAT_PADRAO = -25.2637;
  private static readonly LNG_PADRAO = -57.5759;

  // --- Grid de produtos (coluna esquerda) ---
  produtos: any[] = [];
  loading = false;
  paginaAtual = 0;
  tamanhoPagina = 10;
  totalElements = 0;

  // Opções do seletor "Itens por página" (formato {label, value} igual às
  // demais telas). Mesmos valores que antes viviam em [rowsPerPageOptions].
  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];
  private _ultimoTamanhoPagina = 10;

  produtoNaoEncontrado = false;
  private termoBusca = '';

  /**
   * Codigo de barras que ja entrou no carrinho sozinho. Sem esta marca, qualquer
   * recarga da grade com o MESMO termo (trocar de pagina, marcar "mostrar sem
   * saldo") adicionaria o item de novo.
   */
  private codigoJaAutoAdicionado = '';

  /**
   * Marca que a adicao em andamento veio do codigo de barras colado/bipado, para
   * `onProdutoSemOpcoes`/`onPersonalizacaoConfirmada`/`onPersonalizacaoCancelada`
   * saberem se devem limpar o campo de busca e devolver o foco quando o fluxo
   * (sincrono ou via dialog de variante/modificador) terminar.
   */
  private aguardandoResultadoAutoAdicaoPorCodigo = false;

  /**
   * Quantidade do multiplicador ("3*401") aguardando a confirmacao do item.
   *
   * Existe porque quem decide se o item tem variantes e o dialog de
   * personalizacao, ja depois do clique: a quantidade fica aqui ate o
   * `onProdutoSemOpcoes` poder aplica-la. Volta a 1 logo em seguida.
   */
  private quantidadeMultiplicadorPendente = 1;

  /** Multiplicador digitado e confirmado com Enter, aguardando a resposta da busca. */
  private multiplicadorDoEnter: { quantidade: number; codigo: string } | null = null;
  private buscaSubject = new Subject<void>();
  private imagensComErro = new Set<string>();

  // Filtros (chips de categoria + checkbox de saldo).
  grupos: any[] = [];
  categoriaSelecionada: any = { id: null, nome: 'Todas' };
  mostrarSemSaldo = false;

  // --- Valor de venda de item com preço 0 (paridade com a venda) ---
  produtoParaDefinirPreco: any = null;
  mostrarDialogPreco = false;
  novoPrecoDeVenda: number | null = null;

  // --- Moeda (formatação) ---
  moedaPadrao: 'BRL' | 'PYG' | 'USD' | 'ARS' = 'PYG';
  localeMoeda = 'es-PY';
  digitsMoeda = '1.0-0';
  private moeda = 'GUARANI'; // enum enviado ao backend no POST /pedidos

  // --- Cliente (coluna direita) ---
  clienteSelectorConfig!: EntitySelectorConfig;
  clienteSelecionado: PessoaDTO | undefined = undefined;
  mostrarDialogNovoCliente = false;

  // ---------------------------------------------------------------------------
  // Qual codigo aparece embaixo do nome do item
  //
  // UMA escolha para a tela inteira: vale para o card da grade e para a linha do
  // carrinho. Nasce em NENHUM — a maioria vende pelo nome e o codigo so poluiria
  // o card.
  // ---------------------------------------------------------------------------

  readonly STORAGE_CODIGO = 'criar-pedido-codigo';

  exibicaoCodigo: ExibicaoCodigo = 'NENHUM';
  codigoOptions: { label: string; value: ExibicaoCodigo }[] = [];

  // --- Carrinho ---
  carrinho: LinhaPedido[] = [];

  // Produto aguardando seleção de variante/modificadores (dispara o dialog).
  // Quando o produto não tem opções, o dialog emite (semOpcoes) e adicionamos direto.
  produtoParaPersonalizar: any = null;

  // --- Observação por item (diálogo) ---
  mostrarDialogObservacao = false;
  observacaoEmEdicao = '';
  private linhaObservacaoEmEdicao: LinhaPedido | null = null;

  // --- Confirmação / modalidade ---
  mostrarConfirmacao = false;
  modalidade: Modalidade = 'LOCAL';
  modalidadeOptions: { label: string; value: Modalidade }[] = [];
  observacao = '';
  salvando = false;

  // Forma de pagamento (só relevante quando delivery) — paridade com o mobile e o
  // cardápio web. O Pedido não tem campo estruturado para "Tarjeta" (só
  // `condicaoDeRecebimento`), então a escolha é anexada à observação do pedido.
  formaPagamento: FormaDePagamentoPedido = 'DINHEIRO';
  formaPagamentoOptions: { label: string; value: FormaDePagamentoPedido; icon: string }[] = [];

  // --- Pagamento do pedido ---

  /**
   * Conta onde o dinheiro cobrado por QR Code entra, escolhida em Dados de recebimento.
   *
   * `null` quando a empresa ainda não vinculou: aí não há cobrança nenhuma — o pedido é criado
   * com a forma QR Code registrada e o pagamento combinado depois, como no dinheiro.
   */
  contaQrCode: { id: number; label: string } | null = null;
  private contaQrCodeId: number | null = null;
  private contasBancariasGuarani: any[] = [];

  /**
   * Cobranca por QR do pedido recem-criado. Enquanto existir, a tela fica aguardando a Bancard:
   * o pedido so e faturado quando o pagamento for confirmado, igual ao PDV.
   */
  cobrancaQrCode: { intencaoId: number; qrData?: string | null; urlQr?: string | null } | null = null;
  qrCodeConfirmado = false;
  gerandoQrCode = false;

  /** Bloco do QR dentro do dialogo de confirmacao — alvo do scroll automatico. */
  @ViewChild('blocoQrCodePedido') blocoQrCodePedido?: ElementRef<HTMLElement>;
  private pollingQrCodeSub?: Subscription;

  /** Campo de busca de produtos — usado para limpar e refocar apos adicao automatica por codigo de barras. */
  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('inputNovoPreco') inputNovoPrecoRef?: InputNumber;

  /**
   * Formas habilitadas pela empresa na configuração do minishop.
   * Esta tela segue as MESMAS regras de exibição do checkout da loja.
   */
  /**
   * O que o pedido de consumo no local exige, conforme a loja configurou no minishop.
   * Duas opções independentes: restaurante de mesa usa a mesa, balcão usa a comanda.
   */
  exigenciasDoPedido = {
    numeroDaMesa: false,
    numeroDaComanda: false,
  };

  formasHabilitadas = {
    exibir: false,
    dinheiro: true,
    tarjeta: false,
    qrCode: false,
    transferencia: false,
  };

  // Rebuild das opções traduzidas ao trocar de idioma.
  private langChangeSub?: Subscription;

  // Mesa / comanda (só no consumo local).
  mesa = '';
  comanda = '';

  // --- Delivery (só quando modalidade === 'DELIVERY') ---
  enderecoEntregaRua = '';
  numeroEntrega = '';
  complementoEntrega = '';
  referenciaEntrega = '';

  // Cidade da entrega — só refina a busca do endereço (a mesma rua pode existir em
  // várias cidades). NÃO é enviada como campo estruturado no POST /pedidos; entra
  // apenas no termo de geocoding. Paridade com o mobile.
  cidadesEntrega: { label: string; value: number }[] = [];
  cidadeEntregaSelecionada: { label: string; value: number } | undefined;
  // Cidade padrão da empresa (fallback quando o cliente não tem cidade).
  private cidadeEmpresaId: number | null = null;
  latitudeEntrega: number | null = null;
  longitudeEntrega: number | null = null;
  valorFrete = 0;
  obtendoLocalizacao = false;
  geocodificando = false;

  // Configuração de cobertura de envio do minishop (origem + tipo de preço).
  private tipoCoberturaEnvio = 'SEM_PRECO';
  private latitudeOrigem: number | null = null;
  private longitudeOrigem: number | null = null;
  private freteConfigCarregada = false;

  // Mapa Leaflet (carregado sob demanda).
  private L: any;
  private map: any;
  private marker: any;
  private geocodingTimeout: any = null;

  get ehDelivery(): boolean {
    return this.modalidade === 'DELIVERY';
  }

  /** Modalidade escolhida na tela -> tipo de entrega gravado no pedido. */
  private tipoEntregaDaModalidade(): string {
    if (this.modalidade === 'DELIVERY') return 'DELIVERY';
    if (this.modalidade === 'LOCAL') return 'CONSUMO_NO_LOCAL';
    return 'RETIRADA_NO_LOCAL';
  }

  constructor(
    private itemsService: ItemsService,
    private grupoService: GrupoService,
    private pedidoService: PedidoService,
    private empresaConfigService: EmpresaConfigService,
    private messageService: MessageService,
    private translate: TranslateService,
    private permissaoService: PermissaoService,
    private cidadeService: CidadeService,
    private contaService: ContaService,
    private intencaoDeRecebimentoService: IntencaoDeRecebimentoService,
  ) {}

  ngOnInit(): void {
    this.idEmpresa = sessionStorage.getItem('id') || '';
    this.categoriaSelecionada = { id: null, nome: this.translate.instant('criar_pedido.todas_categorias') };
    this.montarConfigCliente();
    this.montarModalidadeOptions();
    this.montarFormaPagamentoOptions();
    this.montarCodigoOptions();
    this.carregarPreferenciasDeCodigo();
    this.carregarConfigEmpresa();
    this.carregarCidades();
    this.carregarContaDoQrCode();
    this.carregarGrupos();
    this.carregarProdutos();

    this.buscaSubject.pipe(debounceTime(350)).subscribe(() => {
      this.paginaAtual = 0;
      this.carregarProdutos();
    });

    // Reconstrói as opções traduzidas (modalidade e forma de pagamento) ao trocar de idioma.
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.montarModalidadeOptions();
      this.montarFormaPagamentoOptions();
      this.montarCodigoOptions();
    });
  }

  ngOnDestroy(): void {
    this.destruirMapa();
    this.buscaSubject.complete();
    this.langChangeSub?.unsubscribe();
    // Sair da tela abandona a cobranca: alem de parar o polling, ela precisa ser cancelada,
    // senao o QR continua pagavel sem ninguem esperando por ele.
    this.descartarCobrancaQrCode();
  }

  // ===========================================================================
  // Pagamento do pedido
  // ===========================================================================

  /**
   * Dinheiro, cartão na maquininha e transferência só ficam REGISTRADOS no pedido: a nota deles
   * sai no PDV, quando o operador faturar. Por isso a tela não pede mais a conta de destino nem
   * o valor recebido — nada disso teria efeito aqui. Só o QR Code cobra na hora, e a conta dele
   * vem de Dados de recebimento.
   */
  get formaCobraNaHora(): boolean {
    return this.exibirFormasPagamento && this.formaPagamento === 'QR_CODE';
  }

  /**
   * QR Code so deixa confirmar depois de o pagamento entrar. O pedido nasce ja pago; sem esta
   * guarda ele viraria pedido faturado sem dinheiro correspondente.
   */
  get pagamentoQrCodePronto(): boolean {
    if (!this.exibirFormasPagamento || this.formaPagamento !== 'QR_CODE') return true;
    // Sem conta de recebimento não há QR para esperar: o pedido é criado com a forma registrada
    // e sem faturar, e o lojista combina o pagamento com o cliente.
    if (!this.contaQrCode) return true;
    return this.qrCodeConfirmado;
  }

  selecionarFormaPagamento(valor: FormaDePagamentoPedido): void {
    this.formaPagamento = valor;
    // Trocar de forma descarta a cobranca por QR: ela foi gerada para um total e uma conta que
    // podem nao valer mais.
    this.descartarCobrancaQrCode();
  }

  /**
   * Descarta a cobranca por QR em aberto e a cancela no backend.
   *
   * Limpar a tela nao basta: o QR ja lido pelo cliente continua valido na Bancard, e um pagamento
   * que chegasse depois seria confirmado como se o pedido ainda estivesse de pe. Cancelada, a
   * cobranca passa a recusar o pagamento — e o valor volta para o cliente.
   *
   * Pagamento ja confirmado nao se cancela: o dinheiro entrou e e dele que o pedido nasce.
   */
  private descartarCobrancaQrCode(): void {
    const intencaoId = this.cobrancaQrCode?.intencaoId;
    const jaConfirmado = this.qrCodeConfirmado;

    this.pararPollingQrCode();
    this.cobrancaQrCode = null;
    this.qrCodeConfirmado = false;

    if (intencaoId == null || jaConfirmado) return;

    // Sem alerta na tela: o operador ja seguiu adiante e nao ha nada que ele possa fazer aqui.
    // O backend recusa o pagamento pelo status da cobranca, nao pelo aviso.
    this.intencaoDeRecebimentoService.cancelar(intencaoId).subscribe({ error: () => {} });
  }

  /**
   * Carrega as contas de banco em guaranis, para dar nome à conta configurada do QR Code.
   *
   * A configuração (id) vem da sessão e a lista vem de `/conta/banco-guarani`: as duas cargas se
   * encontram em `montarContaDoQrCode`.
   */
  private carregarContaDoQrCode(): void {
    this.contaService.listarContasBancariasEmGuarani().subscribe({
      next: (contas: any[]) => {
        this.contasBancariasGuarani = contas ?? [];
        this.montarContaDoQrCode();
      },
      error: () => {
        this.contasBancariasGuarani = [];
        this.montarContaDoQrCode();
      },
    });
  }

  /** Junta a conta configurada com a lista, para a tela exibir o nome dela. */
  private montarContaDoQrCode(): void {
    const conta = this.contasBancariasGuarani.find((c: any) => c.id === this.contaQrCodeId);
    this.contaQrCode = conta ? { id: conta.id, label: conta.tituloDaConta } : null;
  }

  /** Gera a cobranca por QR ANTES de confirmar o pedido, pelo mesmo endpoint da venda. */
  gerarQrCode(): void {
    if (this.gerandoQrCode || !this.contaQrCode) return;

    this.gerandoQrCode = true;
    this.intencaoDeRecebimentoService
      .gerarQrCode(this.totalComFrete, toLocalISOString(new Date()), this.clienteSelecionado?.id)
      .subscribe({
        next: (intencao) => {
          this.gerandoQrCode = false;
          this.cobrancaQrCode = {
            intencaoId: intencao.id,
            qrData: intencao.qrData,
            urlQr: intencao.urlQr,
          };
          this.qrCodeConfirmado = false;
          this.rolarAteOQrCode();
          this.iniciarPollingQrCode(intencao.id);
        },
        error: (err) => {
          this.gerandoQrCode = false;
          this.messageService.add({
            severity: 'error',
            summary: err?.error?.message || this.translate.instant('criar_pedido.qrcode_erro_gerar'),
          });
        },
      });
  }

  /**
   * Acompanha a confirmacao do pagamento por QR.
   *
   * O pedido so e criado DEPOIS da confirmacao, entao este polling e o que libera o botao de
   * confirmar — nao e mero aviso.
   */
  private iniciarPollingQrCode(intencaoId: number): void {
    this.pararPollingQrCode();
    this.pollingQrCodeSub = interval(5000)
      .pipe(switchMap(() => this.intencaoDeRecebimentoService.consultarStatus(intencaoId)))
      .subscribe({
        next: (status) => {
          if (!status?.confirmado) return;
          this.qrCodeConfirmado = true;
          this.rolarAteOQrCode();
          this.pararPollingQrCode();
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('criar_pedido.qrcode_confirmado'),
          });
        },
        // Falha de rede no polling nao vira alerta: a proxima tentativa resolve.
        error: () => {},
      });
  }

  /**
   * Traz o QR para o centro da tela assim que ele e gerado (e de novo na confirmacao).
   *
   * Quem rola e o corpo do dialogo de confirmacao, nao a pagina: `scrollIntoView` cuida disso
   * sozinho, pois age em todos os containers rolaveis acima do elemento. O `setTimeout` espera
   * o *ngIf renderizar o bloco — sem ele o ViewChild ainda nao existe.
   */
  private rolarAteOQrCode(): void {
    setTimeout(() => {
      this.blocoQrCodePedido?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  private pararPollingQrCode(): void {
    this.pollingQrCodeSub?.unsubscribe();
    this.pollingQrCodeSub = undefined;
  }

  /** Fecha o dialogo do QR e volta a tela para o proximo pedido. */
  concluirCobrancaQrCode(): void {
    this.descartarCobrancaQrCode();
    this.mostrarConfirmacao = false;
    this.destruirMapa();
    this.resetar();
    this.carregarProdutos();
  }

  private montarConfigCliente(): void {
    // Mesma configuração da tela de Vendas: autocomplete rico com opção
    // "Agregar Persona" dentro do dropdown (habilitada pela permissão de cliente)
    // que abre o diálogo de cadastro de pessoa.
    this.clienteSelectorConfig = {
      categoryFilter: 'categoriaCliente=true',
      placeholder: this.translate.instant('criar_pedido.cliente_placeholder'),
      labelIcon: 'pi pi-user',
      showAddButton: false,
      showAddInDropdown: this.permissaoService.temPermissao('cliente'),
      dialogTitle: this.translate.instant('criar_pedido.selecao_cliente_titulo'),
      dialogSubtitle: this.translate.instant('criar_pedido.selecao_cliente_desc'),
      dialogIcon: 'pi pi-users',
    };
  }

  // ---------------------------------------------------------------------------
  // Novo cliente (diálogo de cadastro de pessoa, igual à tela de Vendas)
  // ---------------------------------------------------------------------------

  abrirDialogNovoCliente(): void {
    this.mostrarDialogNovoCliente = true;
  }

  /** Pessoa nova cadastrada via PessoaAdicionarDialogComponent. */
  onClienteCriado(pessoa: PessoaDTO): void {
    this.clienteSelecionado = pessoa;
  }

  /** Pessoa já existente encontrada via RUC no diálogo de cadastro. */
  onClienteVinculado(pessoa: PessoaDTO): void {
    if (pessoa?.id != null) {
      this.clienteSelecionado = pessoa;
    }
  }

  private montarModalidadeOptions(): void {
    this.modalidadeOptions = [
      { label: this.translate.instant('criar_pedido.modalidade_local'), value: 'LOCAL' },
      { label: this.translate.instant('criar_pedido.modalidade_retirada'), value: 'RETIRADA' },
      { label: this.translate.instant('criar_pedido.modalidade_delivery'), value: 'DELIVERY' },
    ];
  }

  /** A empresa pergunta a forma de pagamento (configuração do minishop). */
  get exibirFormasPagamento(): boolean {
    return this.formasHabilitadas.exibir && this.formaPagamentoOptions.length > 0;
  }

  /**
   * Monta as opções a partir do que a empresa habilitou na configuração do minishop.
   *
   * Sem "Perguntar a forma de pagamento" ligado, a seção nem aparece e o pedido
   * segue sem forma — é o que a configuração diz.
   */
  private montarFormaPagamentoOptions(): void {
    if (!this.formasHabilitadas.exibir) {
      this.formaPagamentoOptions = [];
      return;
    }

    const catalogo: { label: string; value: FormaDePagamentoPedido; icon: string; habilitada: boolean }[] = [
      {
        label: this.translate.instant('criar_pedido.forma_pagamento_efectivo'),
        value: 'DINHEIRO',
        icon: 'pi pi-money-bill',
        habilitada: this.formasHabilitadas.dinheiro,
      },
      {
        label: this.translate.instant('criar_pedido.forma_pagamento_tarjeta'),
        value: 'TARJETA',
        icon: 'pi pi-credit-card',
        habilitada: this.formasHabilitadas.tarjeta,
      },
      {
        label: this.translate.instant('criar_pedido.forma_pagamento_qrcode'),
        value: 'QR_CODE',
        icon: 'pi pi-qrcode',
        habilitada: this.formasHabilitadas.qrCode,
      },
      {
        label: this.translate.instant('criar_pedido.forma_pagamento_transferencia'),
        value: 'TRANSFERENCIA',
        icon: 'pi pi-arrow-right-arrow-left',
        habilitada: this.formasHabilitadas.transferencia,
      },
    ];

    this.formaPagamentoOptions = catalogo
      .filter(opcao => opcao.habilitada)
      .map(({ label, value, icon }) => ({ label, value, icon }));

    // A forma escolhida pode ter sido desligada pela empresa: cai na primeira disponível.
    if (!this.formaPagamentoOptions.some(o => o.value === this.formaPagamento)) {
      this.formaPagamento = this.formaPagamentoOptions[0]?.value ?? 'DINHEIRO';
    }
  }

  /** Seleciona a forma de pagamento (delivery). */
  private carregarConfigEmpresa(): void {
    this.empresaConfigService.getSessao().subscribe({
      next: (empresa: any) => {
        // Esta tela segue as mesmas regras de exibição configuradas para o minishop.
        this.formasHabilitadas = {
          exibir: empresa?.minishopExibirFormasDePagamento === true,
          dinheiro: empresa?.minishopAceitaDinheiro !== false,
          tarjeta: empresa?.minishopAceitaTarjeta === true,
          qrCode: empresa?.minishopAceitaQrCode === true,
          transferencia: empresa?.minishopAceitaTransferencia === true,
        };
        this.exigenciasDoPedido = {
          numeroDaMesa: empresa?.minishopExigirNumeroDaMesa === true,
          numeroDaComanda: empresa?.minishopExigirNumeroDaComanda === true,
        };
        this.montarFormaPagamentoOptions();

        this.contaQrCodeId = empresa?.minishopContaQrCodeId ?? null;
        this.montarContaDoQrCode();

        const moeda = empresa?.moeda;
        this.moeda = (typeof moeda === 'string' ? moeda : moeda?.enum) || 'GUARANI';

        const codigoOperacao =
          moeda?.codigoOperacao ??
          (this.moeda === 'REAL' ? 'BRL' : this.moeda === 'DOLAR' ? 'USD' : 'PYG');
        this.moedaPadrao = codigoOperacao as 'BRL' | 'PYG' | 'USD' | 'ARS';
        const rawLocal = moeda?.pais?.local || 'es_PY';
        this.localeMoeda = String(rawLocal).replace('_', '-');
        const casas = Number(moeda?.casasDecimais ?? (codigoOperacao === 'PYG' ? 0 : 2));
        this.digitsMoeda = casas === 0 ? '1.0-0' : '1.2-2';

        if (!this.clienteSelecionado && empresa?.clientePadrao?.id) {
          this.clienteSelecionado = empresa.clientePadrao;
        }

        // Cidade padrão da empresa: fallback da pré-seleção quando o cliente não tem cidade.
        this.cidadeEmpresaId = empresa?.cidade?.id != null ? Number(empresa.cidade.id) : null;
      },
      error: () => {
        this.moeda = 'GUARANI';
      },
    });
  }

  /**
   * Carrega a lista de cidades (mesmo endpoint usado no cadastro de pessoa e na
   * configuração da empresa). Alimenta o dropdown com filtro por nome no delivery.
   */
  private carregarCidades(): void {
    this.cidadeService.get('/cidade?listarTodos=true').subscribe({
      next: (response) => {
        this.cidadesEntrega = (response?.content ?? []).map((cidade) => ({
          label: cidade.nome,
          value: Number(cidade.id),
        }));
        // A lista pode ter carregado depois de o operador entrar no delivery: nesse
        // caso, pré-seleciona agora (se ainda não houver cidade escolhida).
        if (this.ehDelivery && !this.cidadeEntregaSelecionada) {
          this.preselecionarCidadeEntrega();
        }
      },
      error: () => {
        this.cidadesEntrega = [];
      },
    });
  }

  /**
   * Pré-seleciona a cidade da entrega: usa a cidade do cliente selecionado
   * (`cidade_id`) e, como fallback, a cidade padrão da empresa. Não faz nada quando
   * nenhuma das duas está disponível ou quando a lista ainda não carregou.
   */
  private preselecionarCidadeEntrega(): void {
    // A busca de pessoa devolve a cidade como objeto aninhado (`cidade: { id }`),
    // não como `cidade_id` flat — por isso lemos os dois. Fallback: cidade da empresa.
    const cliente = this.clienteSelecionado as any;
    const idAlvo = cliente?.cidade?.id ?? cliente?.cidade_id ?? this.cidadeEmpresaId ?? null;
    if (idAlvo == null) return;
    const encontrada = this.cidadesEntrega.find((c) => c.value === Number(idAlvo));
    if (encontrada) {
      this.cidadeEntregaSelecionada = encontrada;
    }
  }

  /** Nome da cidade escolhida (usado para refinar o termo de geocoding). */
  private get nomeCidadeEntrega(): string {
    return this.cidadeEntregaSelecionada?.label || '';
  }

  /** Trocar a cidade reposiciona o pino no mapa (busca silenciosa). */
  onCidadeEntregaChange(): void {
    this.buscarEnderecoNoMapa(true);
  }

  /** Casas decimais para inputs monetários (0 para Guarani). */
  get decimaisMoeda(): number {
    return this.moedaPadrao === 'PYG' ? 0 : 2;
  }

  // ---------------------------------------------------------------------------
  // Grid de produtos
  // ---------------------------------------------------------------------------

  carregarGrupos(): void {
    const labelTodas = this.translate.instant('criar_pedido.todas_categorias');
    this.grupoService.get('/grupo/filtrarGrupo', { status: true }).subscribe({
      next: (res: any) => {
        this.grupos = [{ id: null, nome: labelTodas }, ...(res?.content ?? [])];
      },
      error: () => {
        this.grupos = [{ id: null, nome: labelTodas }];
      },
    });
  }

  carregarProdutos(): void {
    this.loading = true;
    this.produtoNaoEncontrado = false;

    // "3*401" filtra a grade por "401": o multiplicador e para a adicao, nao para
    // a busca. Sem isto a grade nao acharia nada enquanto o operador digita.
    const termo = this.extrairTermoFiltro(this.termoBusca);
    const params: any = {
      page: this.paginaAtual,
      size: this.tamanhoPagina,
      sort: 'codigoItem,asc',
      // Serviço entra mesmo sem saldo: o backend só oculta MERCADORIA sem saldo.
      apenasComSaldo: !this.mostrarSemSaldo,
    };
    if (this.categoriaSelecionada?.id) {
      params.grupoId = this.categoriaSelecionada.id;
    }
    if (termo) {
      params.filter =
        `nome=${termo}&barras=${termo}&codigoItem=${termo}` +
        `&nomeGrupo=${termo}&nomeSubGrupo=${termo}`;
    }

    this.itemsService.get('/item/buscar', params).subscribe({
      next: (res: any) => {
        this.produtos = res?.content ?? [];
        this.totalElements = res?.totalElements ?? 0;
        this.produtoNaoEncontrado = this.produtos.length === 0;
        this.loading = false;
        this.autoAdicionarPorCodigoDeBarras(termo);
        this.resolverMultiplicadorDoEnter(termo);
      },
      error: () => {
        this.produtos = [];
        this.totalElements = 0;
        this.produtoNaoEncontrado = true;
        this.loading = false;
      },
    });
  }

  /**
   * Codigo de barras colado (ou bipado) na busca: o item entra no carrinho sem
   * clique. So dispara quando a busca devolveu UM item e o termo e exatamente o
   * codigo de barras dele — com dois ou mais resultados, ou casando por nome, a
   * escolha continua sendo do operador.
   *
   * Passa pelo mesmo `adicionarProduto()` do clique, entao item com variantes ou
   * modificadores continua abrindo o dialog de personalizacao em vez de entrar
   * cru no carrinho. Esse dialog resolve de forma ASSINCRONA (evento
   * `semOpcoes` ou `confirmar`/`cancelar`), entao a limpeza do campo de busca
   * fica marcada aqui e so e executada quando um desses handlers rodar — ver
   * `aguardandoResultadoAutoAdicaoPorCodigo`.
   */
  private autoAdicionarPorCodigoDeBarras(termo: string): void {
    const t = (termo || '').trim();
    if (!t || t === this.codigoJaAutoAdicionado) return;
    if (this.totalElements !== 1 || this.produtos.length !== 1) return;

    const produto = this.produtos[0];
    const barras = String(produto?.barras ?? '').trim();
    if (!barras || barras.toLowerCase() !== t.toLowerCase()) return;

    // O termo aqui ja vem sem o prefixo; a quantidade sai do termo ORIGINAL.
    const multiplicador = this.parseMultiplicador(this.termoBusca);
    this.codigoJaAutoAdicionado = t;
    this.aguardandoResultadoAutoAdicaoPorCodigo = true;
    const iniciado = this.adicionarProdutoComQuantidade(produto, multiplicador?.quantidade ?? 1);
    // Bloqueado por saldo: nem chegou a chamar `adicionarProduto`, entao nenhum
    // handler assincrono vai rodar — o operador precisa ver o codigo que causou o aviso.
    if (!iniciado) this.aguardandoResultadoAutoAdicaoPorCodigo = false;
  }

  /**
   * Fecha o ciclo aberto em `autoAdicionarPorCodigoDeBarras`. So faz algo quando
   * a adicao em andamento veio do codigo de barras (flag ligada); clique manual
   * no card nunca liga a flag, entao esses handlers seguem neutros pra ele.
   * `entrouNoCarrinho = false` (nao encontrado, cancelado) mantem o campo como
   * esta, para o operador ver o que causou o problema.
   */
  private finalizarAutoAdicaoPorCodigo(entrouNoCarrinho: boolean): void {
    if (!this.aguardandoResultadoAutoAdicaoPorCodigo) return;
    this.aguardandoResultadoAutoAdicaoPorCodigo = false;
    if (entrouNoCarrinho) this.limparBuscaAposAutoAdicao();
  }

  /**
   * Reseta o campo de busca e a listagem apos uma adicao automatica por codigo
   * de barras (colado/bipado), e devolve o foco ao input para o proximo codigo.
   */
  private limparBuscaAposAutoAdicao(): void {
    this.termoBusca = '';
    this.codigoJaAutoAdicionado = '';
    this.paginaAtual = 0;

    if (this.searchInputRef?.nativeElement) {
      this.searchInputRef.nativeElement.value = '';
    }

    this.carregarProdutos();

    setTimeout(() => this.searchInputRef?.nativeElement?.focus(), 0);
  }

  /**
   * Adiciona respeitando o SALDO: pede 5 de um item com 2 em estoque e entram 2,
   * com aviso. Mercadoria sem saldo nao entra; servico nao tem limite.
   *
   * A quantidade fica pendente porque o caminho passa pelo dialog de
   * personalizacao (ver `quantidadeMultiplicadorPendente`).
   *
   * Retorna `true` quando o item efetivamente entrou no carrinho, `false` nos
   * casos bloqueados por saldo — usado pela adicao automatica por codigo de
   * barras para so limpar o campo de busca quando algo de fato foi adicionado.
   */
  private adicionarProdutoComQuantidade(produto: any, quantidade: number): boolean {
    if (!produto?.id) return false;

    if (this.ehServico(produto)) {
      this.quantidadeMultiplicadorPendente = Math.max(quantidade, 1);
      this.adicionarProduto(produto);
      return true;
    }

    const saldo = Number(produto.saldo ?? 0);
    if (saldo <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.translate.instant('criar_pedido.toast_sem_saldo'),
      });
      return false;
    }

    const noCarrinho = this.carrinho
      .filter((c) => c.itemId === produto.id)
      .reduce((acc, c) => acc + Number(c.quantidade ?? 0), 0);
    const espaco = Math.max(saldo - noCarrinho, 0);
    if (espaco <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.translate.instant('criar_pedido.toast_limite_saldo', { saldo }),
      });
      return false;
    }

    const efetiva = Math.min(Math.max(quantidade, 1), espaco);
    this.quantidadeMultiplicadorPendente = efetiva;
    this.adicionarProduto(produto);

    if (efetiva < quantidade) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.translate.instant('criar_pedido.toast_limite_saldo', { saldo }),
      });
      return true;
    }

    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('criar_pedido.toast_adicionado'),
      detail: efetiva > 1
        ? this.translate.instant('criar_pedido.toast_produto_adicionado_qtd', {
            quantidade: efetiva,
            produto: produto?.nome ?? '',
            producto: produto?.nome ?? '',
          })
        : this.translate.instant('criar_pedido.toast_produto_adicionado', {
            produto: produto?.nome ?? '',
            producto: produto?.nome ?? '',
          }),
    });
    return true;
  }

  private montarCodigoOptions(): void {
    this.codigoOptions = [
      { label: this.translate.instant('criar_pedido.codigo_nenhum'), value: 'NENHUM' },
      { label: this.translate.instant('criar_pedido.codigo_interno'), value: 'CODIGO' },
      { label: this.translate.instant('criar_pedido.codigo_barras'), value: 'BARRAS' },
    ];
  }

  private carregarPreferenciasDeCodigo(): void {
    try {
      const salvo = localStorage.getItem(this.STORAGE_CODIGO) as ExibicaoCodigo | null;
      if (salvo === 'NENHUM' || salvo === 'CODIGO' || salvo === 'BARRAS') {
        this.exibicaoCodigo = salvo;
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencia de exibicao de codigo:', e);
    }
  }

  onExibicaoCodigoChange(): void {
    try {
      localStorage.setItem(this.STORAGE_CODIGO, this.exibicaoCodigo);
    } catch (e) {
      console.warn('Erro ao salvar preferencia de exibicao de codigo:', e);
    }
  }

  /**
   * Texto do codigo para a grade e para o carrinho. Devolve null quando nao ha
   * nada a mostrar (escolha NENHUM, ou item sem o codigo escolhido), e o
   * template usa isso para nem renderizar a linha.
   */
  textoDoCodigo(item: any, modo: ExibicaoCodigo): string | null {
    if (!item || modo === 'NENHUM') return null;
    if (modo === 'BARRAS') {
      const barras = String(item.barras ?? '').trim();
      return barras || null;
    }
    const codigo = item.codigoItem;
    if (codigo == null || String(codigo).trim() === '') return null;
    return `${this.translate.instant('criar_pedido.cod')} ${codigo}`;
  }

  onPaginatorChange(event: any): void {
    this.paginaAtual = event.page ?? 0;
    this.tamanhoPagina = event.rows ?? this.tamanhoPagina;
    this._ultimoTamanhoPagina = this.tamanhoPagina;
    this.carregarProdutos();
  }

  /**
   * Troca do "Itens por página" (dropdown rotulado ao lado do paginator, igual
   * às demais telas). Volta para a primeira página: manter o índice antigo com
   * uma página maior pode cair fora do total e devolver lista vazia.
   * A guarda evita recarregar quando o valor não mudou de fato.
   */
  onRowsPerPageChange(): void {
    if (this.tamanhoPagina === this._ultimoTamanhoPagina) return;
    this._ultimoTamanhoPagina = this.tamanhoPagina;
    this.paginaAtual = 0;
    this.carregarProdutos();
  }

  selecionarCategoria(grupo: any): void {
    this.categoriaSelecionada = grupo;
    this.paginaAtual = 0;
    this.carregarProdutos();
  }

  onToggleSemSaldo(): void {
    this.paginaAtual = 0;
    this.carregarProdutos();
  }

  onInputChange(event: Event): void {
    this.termoBusca = (event.target as HTMLInputElement).value ?? '';
    this.codigoJaAutoAdicionado = '';
    this.buscaSubject.next();
  }

  onSearchEnter(ev: Event): void {
    this.termoBusca = ((ev.target as HTMLInputElement)?.value || '').trim();
    this.codigoJaAutoAdicionado = '';
    this.paginaAtual = 0;
    // "3*401" no Enter: a busca sai pelo codigo e a adicao acontece quando a
    // resposta chegar (ver `resolverMultiplicadorDoEnter`).
    this.multiplicadorDoEnter = this.parseMultiplicador(this.termoBusca);
    this.carregarProdutos();
  }

  /**
   * Fecha o ciclo do multiplicador confirmado com Enter: com UM item batendo
   * exatamente o codigo (interno ou de barras), ele entra com a quantidade
   * pedida, limitada ao saldo. Com dois ou mais, a grade fica na tela e a
   * escolha continua do operador.
   */
  private resolverMultiplicadorDoEnter(termo: string): void {
    const multiplicador = this.multiplicadorDoEnter;
    this.multiplicadorDoEnter = null;
    if (!multiplicador) return;
    // Ja resolvido pela adicao automatica de codigo de barras.
    if (termo && termo === this.codigoJaAutoAdicionado) return;

    const codigo = multiplicador.codigo.toLowerCase();
    const exatos = this.produtos.filter(
      (p: any) =>
        String(p?.codigoItem ?? '').toLowerCase() === codigo ||
        String(p?.barras ?? '').toLowerCase() === codigo,
    );
    if (exatos.length !== 1) return;

    this.codigoJaAutoAdicionado = termo;
    this.adicionarProdutoComQuantidade(exatos[0], multiplicador.quantidade);
  }

  // ---------------------------------------------------------------------------
  // Multiplicador "<qtd>*<codigo>"
  // ---------------------------------------------------------------------------

  /** "3*401" -> { quantidade: 3, codigo: '401' }. Qualquer outra forma e null. */
  private parseMultiplicador(termo: string): { quantidade: number; codigo: string } | null {
    const match = /^(\d+)\*(\w+)$/.exec((termo || '').trim());
    if (!match) return null;
    const quantidade = parseInt(match[1], 10);
    const codigo = match[2];
    if (!Number.isFinite(quantidade) || quantidade < 1 || !codigo) return null;
    return { quantidade, codigo };
  }

  /** Parte do termo que vai para a busca: sem o prefixo "<qtd>*". */
  private extrairTermoFiltro(termo: string): string {
    const t = (termo || '').trim();
    const match = /^\d+\*(.*)$/.exec(t);
    return (match ? match[1] : t).trim();
  }

  ehServico(p: any): boolean {
    const tip = p?.tipoItem;
    const codigo = typeof tip === 'string' ? tip : tip?.enum;
    return codigo === 'SERVICO';
  }

  indisponivel(p: any): boolean {
    if (this.ehServico(p)) return false;
    return Number(p?.saldo ?? 0) <= 0;
  }

  podeClicar(p: any): boolean {
    return !this.indisponivel(p);
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    const produtoId = img.dataset['productId'];
    if (produtoId) {
      this.imagensComErro.add(produtoId);
    }
  }

  temErroImagem(produto: any): boolean {
    return this.imagensComErro.has(String(produto.id));
  }

  // ---------------------------------------------------------------------------
  // Carrinho
  // ---------------------------------------------------------------------------

  /**
   * Clique no produto da grade. Dispara a verificação de variantes/modificadores
   * pelo dialog reaproveitado do PDV: se o produto tiver opções, o dialog abre;
   * caso contrário, o dialog emite (semOpcoes) e o produto é adicionado direto.
   */
  adicionarProduto(produto: any): void {
    if (!produto?.id) return;
    // Preço 0: pede o valor de venda antes (igual à venda); o item entra sem
    // passar pela personalização.
    if (Number(produto.precoDeVenda ?? 0) <= 0) {
      this.produtoParaDefinirPreco = produto;
      this.novoPrecoDeVenda = null;
      this.mostrarDialogPreco = true;
      return;
    }
    // Setar (do valor anterior nulo) dispara o ngOnChanges do dialog.
    this.produtoParaPersonalizar = produto;
  }

  /** Produto sem variantes/modificadores: adiciona direto (com merge por quantidade). */
  onProdutoSemOpcoes(produto: any): void {
    this.produtoParaPersonalizar = null;
    if (!produto?.id) {
      this.finalizarAutoAdicaoPorCodigo(false);
      return;
    }
    const saldo = Number(produto.saldo ?? 0);
    const controlaSaldo = !this.ehServico(produto);
    const limitar = (qtd: number) => (controlaSaldo ? Math.min(qtd, saldo || qtd) : qtd);
    // Quantidade do multiplicador, quando houve; consumida aqui e zerada em
    // seguida, para o proximo clique voltar a valer 1.
    const aAdicionar = Math.max(this.quantidadeMultiplicadorPendente, 1);
    this.quantidadeMultiplicadorPendente = 1;
    // Merge só entre linhas do MESMO produto e AMBAS sem personalização.
    const existente = this.carrinho.find(
      (c) => c.itemId === produto.id && !this.temPersonalizacao(c),
    );
    if (existente) {
      existente.quantidade = limitar(existente.quantidade + aAdicionar);
    } else {
      this.carrinho = [
        ...this.carrinho,
        {
          itemId: produto.id,
          nome: produto.nome,
          codigoItem: produto.codigoItem,
          barras: produto.barras,
          valorUnitario: Number(produto.precoDeVenda ?? 0),
          quantidade: limitar(aAdicionar),
          saldo,
          controlaSaldo,
        },
      ];
    }
    this.finalizarAutoAdicaoPorCodigo(true);
  }

  /** Personalização confirmada no dialog: cada seleção vira uma linha própria (nunca soma). */
  onPersonalizacaoConfirmada(personalizacao: PersonalizacaoDeItemSelecionada): void {
    const produto = this.produtoParaPersonalizar;
    this.produtoParaPersonalizar = null;
    // Item personalizado: cada selecao e uma linha propria, entao o
    // multiplicador nao se aplica.
    this.quantidadeMultiplicadorPendente = 1;
    if (!produto?.id) {
      this.finalizarAutoAdicaoPorCodigo(false);
      return;
    }

    const saldo = Number(produto.saldo ?? 0);
    this.carrinho = [
      ...this.carrinho,
      {
        itemId: produto.id,
        nome: produto.nome,
        codigoItem: produto.codigoItem,
        barras: produto.barras,
        valorUnitario: Number(personalizacao.precoUnitario ?? 0),
        quantidade: 1,
        saldo,
        controlaSaldo: !this.ehServico(produto),
        variantesDePreco: personalizacao.variantesDePreco,
        modificadores: personalizacao.modificadores,
        varianteDescricao: personalizacao.varianteDescricao,
        modificadoresDescricao: personalizacao.modificadoresDescricao,
        observacaoItem: personalizacao.observacao || undefined,
      },
    ];
    this.finalizarAutoAdicaoPorCodigo(true);
  }

  /** Usuário cancelou o dialog de personalização: nada é adicionado. */
  onPersonalizacaoCancelada(): void {
    this.produtoParaPersonalizar = null;
    this.quantidadeMultiplicadorPendente = 1;
    // Nada entrou no carrinho: mantem o campo de busca como esta se a adicao
    // veio do codigo de barras, para o operador ver o que causou o cancelamento.
    this.finalizarAutoAdicaoPorCodigo(false);
  }

  /**
   * Valor informado para o item com preço 0: entra como linha própria com esse
   * valor (nunca soma com outra linha, o preço pode ser diferente a cada vez).
   * Sem valor > 0 o dialog continua aberto, igual à venda.
   */
  confirmarNovoPreco(): void {
    const produto = this.produtoParaDefinirPreco;
    const valor = Number(this.novoPrecoDeVenda ?? 0);
    if (!produto?.id || valor <= 0) return;

    const saldo = Number(produto.saldo ?? 0);
    const controlaSaldo = !this.ehServico(produto);
    const pedida = Math.max(this.quantidadeMultiplicadorPendente, 1);
    this.quantidadeMultiplicadorPendente = 1;

    this.carrinho = [
      ...this.carrinho,
      {
        itemId: produto.id,
        nome: produto.nome,
        codigoItem: produto.codigoItem,
        barras: produto.barras,
        valorUnitario: valor,
        quantidade: controlaSaldo ? Math.min(pedida, saldo || pedida) : pedida,
        saldo,
        controlaSaldo,
      },
    ];
    this.mostrarDialogPreco = false;
    this.produtoParaDefinirPreco = null;
    this.finalizarAutoAdicaoPorCodigo(true);
  }

  /** Foco no campo de valor ao abrir o dialog, para digitar e confirmar com Enter. */
  focarInputPreco(): void {
    setTimeout(() => this.inputNovoPrecoRef?.input?.nativeElement?.focus(), 0);
  }

  /** Fechou sem informar o valor: nada é adicionado. */
  cancelarNovoPreco(): void {
    if (!this.produtoParaDefinirPreco) return;
    this.mostrarDialogPreco = false;
    this.produtoParaDefinirPreco = null;
    this.quantidadeMultiplicadorPendente = 1;
    this.finalizarAutoAdicaoPorCodigo(false);
  }

  /** Uma linha tem personalização quando possui variante e/ou modificadores. */
  temPersonalizacao(linha: LinhaPedido): boolean {
    return !!(linha.variantesDePreco?.length || linha.modificadores?.length);
  }

  // ---------------------------------------------------------------------------
  // Observação por item (paridade com o mobile)
  // ---------------------------------------------------------------------------

  /** Indica se a linha já possui observação manual digitada. */
  temObservacaoManual(linha: LinhaPedido): boolean {
    return !!linha.observacaoManual?.trim();
  }

  /**
   * Observação final enviada ao backend: junta a observação digitada com a
   * descrição da personalização (quando houver), no formato "<manual> | <personalização>".
   */
  private observacaoCompleta(linha: LinhaPedido): string {
    const manual = (linha.observacaoManual || '').trim();
    const personalizacao = (linha.observacaoItem || '').trim();
    if (!manual) return personalizacao;
    if (!personalizacao) return manual;
    return `${manual} | ${personalizacao}`;
  }

  /** Abre o diálogo de observação da linha (pré-preenchido com a observação atual). */
  abrirObservacaoItem(linha: LinhaPedido): void {
    this.linhaObservacaoEmEdicao = linha;
    this.observacaoEmEdicao = linha.observacaoManual || '';
    this.mostrarDialogObservacao = true;
  }

  /** Salva a observação digitada na linha em edição. */
  salvarObservacaoItem(): void {
    if (this.linhaObservacaoEmEdicao) {
      this.linhaObservacaoEmEdicao.observacaoManual = this.observacaoEmEdicao.trim();
    }
    this.mostrarDialogObservacao = false;
  }

  /** Limpa o estado de edição ao fechar o diálogo. */
  fecharObservacaoItem(): void {
    this.linhaObservacaoEmEdicao = null;
    this.observacaoEmEdicao = '';
  }

  incrementarQuantidade(linha: LinhaPedido): void {
    if (!linha.controlaSaldo || linha.quantidade < linha.saldo) {
      linha.quantidade += 1;
    }
  }

  decrementarQuantidade(linha: LinhaPedido): void {
    if (linha.quantidade > 1) {
      linha.quantidade -= 1;
    }
  }

  ajustarQuantidade(linha: LinhaPedido): void {
    if (!linha.quantidade || linha.quantidade < 1) {
      linha.quantidade = 1;
    }
    if (linha.controlaSaldo && linha.quantidade > linha.saldo) {
      linha.quantidade = linha.saldo;
    }
  }

  removerLinha(linha: LinhaPedido): void {
    // Remover por referência: podem existir várias linhas do mesmo produto
    // (personalizações diferentes = linhas separadas).
    this.carrinho = this.carrinho.filter((l) => l !== linha);
  }

  limparCarrinho(): void {
    this.carrinho = [];
  }

  subtotalLinha(linha: LinhaPedido): number {
    return (linha.valorUnitario || 0) * (linha.quantidade || 0);
  }

  calcularSubtotal(): number {
    return this.carrinho.reduce((soma, l) => soma + this.subtotalLinha(l), 0);
  }

  get total(): number {
    return this.calcularSubtotal();
  }

  /** Soma das quantidades de todas as linhas do carrinho (arredondada para evitar ruido de ponto flutuante). */
  get quantidadeTotalItens(): number {
    const soma = this.carrinho.reduce((acc, l) => acc + (Number(l.quantidade) || 0), 0);
    return Math.round(soma * 1000) / 1000;
  }

  /** Total geral (itens + frete quando delivery), como no mobile. */
  get totalComFrete(): number {
    return this.total + (this.ehDelivery ? (Number(this.valorFrete) || 0) : 0);
  }

  // ---------------------------------------------------------------------------
  // Modalidade / Delivery
  // ---------------------------------------------------------------------------

  onModalidadeChange(): void {
    if (this.ehDelivery) {
      this.garantirConfigFreteECalcular();
      // Pré-seleciona a cidade da entrega (cliente → empresa) ao entrar no delivery.
      this.preselecionarCidadeEntrega();
      // Pré-preenche o endereço de entrega com o endereço padrão do cliente
      // (só se ainda estiver vazio — não sobrescreve o que o operador digitou).
      const preencheu = this.preencherEnderecoDeEntregaDoCliente(false);
      // Espera o *ngIf do bloco de delivery renderizar o container do mapa.
      setTimeout(async () => {
        await this.inicializarMapa();
        // Preenchido a partir do cliente: posiciona o pino no mapa e recalcula o
        // frete via geocoding (silencioso — sem toast de "não encontrado"). Dá um
        // respiro para o mapa assentar (largura do dialog + layout) antes do setView.
        if (preencheu) {
          setTimeout(() => this.buscarEnderecoNoMapa(true), 300);
        }
      }, 250);
    } else {
      this.destruirMapa();
    }
  }

  /**
   * Cliente selecionado no dropdown. Em delivery, atualiza o endereço de entrega
   * com o endereço do novo cliente (sobrescreve, pois a troca foi intencional) e
   * reposiciona o pino no mapa via geocoding silencioso. Paridade com o mobile.
   * Fora de delivery não faz nada — o endereço só é usado no envio.
   */
  onClienteSelecionado(): void {
    if (!this.ehDelivery) return;
    // Troca de cliente atualiza a cidade da entrega para a do novo cliente (fallback empresa).
    this.preselecionarCidadeEntrega();
    if (this.preencherEnderecoDeEntregaDoCliente(true)) {
      this.buscarEnderecoNoMapa(true);
    }
  }

  /**
   * Pré-preenche o endereço de entrega (delivery) com o endereço padrão do
   * cliente selecionado — paridade com o mobile. Retorna true quando preencheu.
   *
   * @param sobrescrever quando false, não sobrescreve um endereço já digitado
   *   pelo operador (só preenche se o campo estiver vazio).
   */
  private preencherEnderecoDeEntregaDoCliente(sobrescrever: boolean): boolean {
    const rua = (this.clienteSelecionado?.endereco || '').trim();
    if (!rua) return false;
    if (!sobrescrever && this.enderecoEntregaRua.trim()) return false;
    this.enderecoEntregaRua = rua;
    this.numeroEntrega = (this.clienteSelecionado?.numero || '').trim();
    return true;
  }

  /**
   * Carrega (uma vez) a configuração de cobertura de envio — tipo de preço +
   * coordenadas de origem — via GET /configuracaoEmpresa/{id} e recalcula o
   * frete. Mesmo comportamento do mobile.
   */
  private garantirConfigFreteECalcular(): void {
    if (this.freteConfigCarregada) {
      this.recalcularFrete();
      return;
    }
    if (!this.idEmpresa) {
      this.freteConfigCarregada = true;
      return;
    }
    this.empresaConfigService.get(`/configuracaoEmpresa/${this.idEmpresa}`).subscribe({
      next: (json: any) => {
        const tipo = json?.tipoPrecoCoberturaDeEnvio;
        this.tipoCoberturaEnvio =
          (tipo && typeof tipo === 'object' ? tipo.enum : tipo) || 'SEM_PRECO';
        this.latitudeOrigem = json?.latitude != null ? Number(json.latitude) : null;
        this.longitudeOrigem = json?.longitude != null ? Number(json.longitude) : null;
        this.freteConfigCarregada = true;
        this.recalcularFrete();
      },
      error: () => {
        this.tipoCoberturaEnvio = 'SEM_PRECO';
        this.freteConfigCarregada = true;
      },
    });
  }

  /**
   * Calcula o valor do frete pelo backend (POST /calculoDeFrete), a partir do
   * tipo de precificação e da distância (origem → ponto de entrega). SEM_PRECO
   * zera; PRECO_FIXO não depende de distância; os tipos por distância exigem
   * origem e ponto de entrega marcados.
   */
  private recalcularFrete(): void {
    if (!this.ehDelivery) return;

    if (this.tipoCoberturaEnvio === 'SEM_PRECO') {
      this.valorFrete = 0;
      return;
    }

    const empresaId = this.idEmpresa ? Number(this.idEmpresa) : null;

    // Com o ponto de entrega marcado, o BACKEND calcula a distância conforme o
    // tipo (raio = linha reta; km = trajeto/rota) e devolve o valor — o cliente
    // não calcula distância, então o preview bate exatamente com a cobrança.
    if (this.latitudeEntrega != null && this.longitudeEntrega != null) {
      this.pedidoService
        .get('/calculoDeFrete/por-coordenadas', {
          empresa_id: empresaId,
          latitude: this.latitudeEntrega,
          longitude: this.longitudeEntrega,
        })
        .subscribe({
          next: (res: any) => {
            const frete = Number(res);
            this.valorFrete = isNaN(frete) ? 0 : frete;
          },
          // Fora da área de cobertura (422) ou erro → sem frete calculado.
          error: () => {
            this.valorFrete = 0;
          },
        });
      return;
    }

    // Sem ponto de entrega marcado: só PRECO_FIXO tem valor (não depende de distância).
    if (this.tipoCoberturaEnvio === 'PRECO_FIXO') {
      this.pedidoService
        .post('/calculoDeFrete', { tipoPrecoCoberturaDeEnvio: 'PRECO_FIXO', empresa_id: empresaId })
        .subscribe({
          next: (res: any) => {
            const frete = Number(res);
            this.valorFrete = isNaN(frete) ? 0 : frete;
          },
          error: () => {
            this.valorFrete = 0;
          },
        });
      return;
    }

    // Distância sem ponto de entrega ainda → 0.
    this.valorFrete = 0;
  }

  /** Concatena rua, número e complemento no formato do minishop web. */
  private montarEnderecoDeEntrega(): string {
    let endereco = this.enderecoEntregaRua.trim();
    const numero = this.numeroEntrega.trim();
    const complemento = this.complementoEntrega.trim();
    if (numero) endereco += `, N ${numero}`;
    if (complemento) endereco += ` - ${complemento}`;
    return endereco;
  }

  // ---------------------------------------------------------------------------
  // Mapa Leaflet
  // ---------------------------------------------------------------------------

  private async inicializarMapa(): Promise<void> {
    const container = document.getElementById('map-entrega-pedido');
    if (!container) return;

    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    if (!this.L) {
      const leafletModule = await import('leaflet');
      this.L = (leafletModule as any).default ?? leafletModule;
      this.fixLeafletIcons();
    }

    const centro: [number, number] = [
      this.latitudeEntrega ?? CriarPedidoComponent.LAT_PADRAO,
      this.longitudeEntrega ?? CriarPedidoComponent.LNG_PADRAO,
    ];

    this.map = this.L.map('map-entrega-pedido', { zoomControl: true }).setView(centro, 15);
    this.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
    }).addTo(this.map);

    this.marker = this.L.marker(centro, { draggable: true, autoPan: true }).addTo(this.map);
    this.marker.on('dragend', () => {
      const pos = this.marker.getLatLng();
      this.atualizarCoordenadasEntrega(pos.lat, pos.lng);
      this.geocodificarReversoComDebounce(pos.lat, pos.lng);
    });
    this.map.on('click', (e: any) => {
      const { lat, lng } = e.latlng;
      this.marker.setLatLng([lat, lng]);
      this.atualizarCoordenadasEntrega(lat, lng);
      this.geocodificarReversoComDebounce(lat, lng);
    });

    this.revalidarMapa();
  }

  /**
   * Revalida o tamanho do mapa várias vezes ao longo de ~1s. Necessário porque o
   * mapa vive dentro de um dialog que abre com animação, muda de largura ao entrar
   * no delivery e tem corpo rolável — se o Leaflet calcular o tamanho num momento
   * transitório, as tiles saem cinzas. Chamado no init e após cada reposicionamento
   * (busca de endereço / usar localização).
   */
  private revalidarMapa(): void {
    [50, 200, 500, 900].forEach((ms) =>
      setTimeout(() => this.map && this.map.invalidateSize(), ms),
    );
  }

  private fixLeafletIcons(): void {
    corrigirIconesLeaflet(this.L);
  }

  private destruirMapa(): void {
    clearTimeout(this.geocodingTimeout);
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    this.marker = null;
  }

  /** Atualiza o ponto de entrega e recalcula o frete quando depende da distância. */
  private atualizarCoordenadasEntrega(lat: number, lng: number): void {
    this.latitudeEntrega = lat;
    this.longitudeEntrega = lng;
    this.recalcularFrete();
  }

  /** Captura a localização via GPS do navegador, centraliza o mapa e geocodifica. */
  usarMinhaLocalizacao(): void {
    if (!navigator.geolocation) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('criar_pedido.localizacao_indisponivel'),
      });
      return;
    }
    this.obtendoLocalizacao = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        this.atualizarCoordenadasEntrega(lat, lng);
        if (this.map && this.marker) {
          this.map.setView([lat, lng], 16);
          this.marker.setLatLng([lat, lng]);
          this.revalidarMapa();
        }
        this.geocodificarReverso(lat, lng);
        this.obtendoLocalizacao = false;
      },
      () => {
        this.obtendoLocalizacao = false;
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('criar_pedido.localizacao_negada'),
        });
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  /**
   * Geocodificação DIRETA (endereço → coordenadas): busca a rua digitada no
   * Nominatim, move o marcador/mapa para o resultado e recalcula o frete. Assim
   * o operador pesquisa por rua e o ponto é destacado no mapa.
   *
   * @param silencioso quando true, não exibe o toast de "endereço não
   *   encontrado" — usado no preenchimento automático a partir do cliente.
   */
  async buscarEnderecoNoMapa(silencioso = false): Promise<void> {
    // Inclui a cidade selecionada (quando houver) para desambiguar a rua e fixa
    // "Paraguay" no fim — a mesma rua pode existir em várias cidades. Paridade com o mobile.
    const partes = [this.enderecoEntregaRua, this.numeroEntrega, this.nomeCidadeEntrega]
      .map((v) => (v || '').trim())
      .filter((v) => v.length > 0);
    if (partes.length === 0) return;
    partes.push('Paraguay');
    const termo = partes.join(', ');

    this.geocodificando = true;
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(termo)}&format=json&addressdetails=1&limit=1`;
      const resp = await fetch(url, { headers: { 'Accept-Language': 'es' } });
      const data = await resp.json();
      if (Array.isArray(data) && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        if (!isNaN(lat) && !isNaN(lng)) {
          this.latitudeEntrega = lat;
          this.longitudeEntrega = lng;
          if (this.map && this.marker) {
            this.map.setView([lat, lng], 16);
            this.marker.setLatLng([lat, lng]);
            // Revalida o tamanho: no pré-preenchimento pelo cliente este setView
            // ocorre logo após o init, antes de o layout do dialog assentar.
            this.revalidarMapa();
          }
          this.recalcularFrete();
        }
      } else if (!silencioso) {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('criar_pedido.endereco_nao_encontrado'),
        });
      }
    } catch {
      // Silencioso: o operador pode ajustar o pino manualmente.
    } finally {
      this.geocodificando = false;
    }
  }

  private geocodificarReversoComDebounce(lat: number, lng: number): void {
    clearTimeout(this.geocodingTimeout);
    this.geocodingTimeout = setTimeout(() => this.geocodificarReverso(lat, lng), 500);
  }

  /** Geocoding reverso (coordenadas → endereço) via Nominatim (OpenStreetMap). */
  private async geocodificarReverso(lat: number, lng: number): Promise<void> {
    this.geocodificando = true;
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`;
      const resp = await fetch(url, { headers: { 'Accept-Language': 'es' } });
      const data = await resp.json();
      if (data?.address) {
        this.enderecoEntregaRua = data.address.road || data.display_name || this.enderecoEntregaRua;
        if (data.address.house_number) {
          this.numeroEntrega = data.address.house_number;
        }
      }
    } catch {
      // Silencioso: o usuário pode preencher manualmente.
    } finally {
      this.geocodificando = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Criar pedido
  // ---------------------------------------------------------------------------

  get podeCriar(): boolean {
    return !this.salvando && !!this.clienteSelecionado?.id && this.carrinho.length > 0;
  }

  abrirConfirmacao(): void {
    if (!this.clienteSelecionado?.id) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('criar_pedido.selecione_cliente'),
      });
      return;
    }
    if (this.carrinho.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('criar_pedido.adicione_item'),
      });
      return;
    }
    this.mostrarConfirmacao = true;
  }

  /**
   * Limpa o mapa ao fechar o diálogo (evita vazamento e mapa órfão) e cancela a cobranca por QR.
   *
   * Fechar o dialogo — no botao Cancelar, no X ou no Esc — e desistir do pedido: a cobranca que
   * estava esperando pagamento morre junto.
   */
  onFecharConfirmacao(): void {
    this.destruirMapa();
    this.descartarCobrancaQrCode();
  }

  /** Valida os campos obrigatórios da modalidade. Delivery exige endereço. */
  private validarAntesDeCriar(): boolean {
    // Mesa e comanda: só no consumo local, e só quando a loja exige.
    if (this.modalidade === 'LOCAL') {
      if (this.exigenciasDoPedido.numeroDaMesa && this.parseInteiroOuNulo(this.mesa) == null) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('criar_pedido.mesa_obrigatoria'),
        });
        return false;
      }
      if (this.exigenciasDoPedido.numeroDaComanda && this.parseInteiroOuNulo(this.comanda) == null) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('criar_pedido.comanda_obrigatoria'),
        });
        return false;
      }
    }

    if (this.ehDelivery && !this.enderecoEntregaRua.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('criar_pedido.delivery_endereco_obrigatorio'),
      });
      return false;
    }
    return true;
  }

  /** Rótulo traduzido da forma de pagamento escolhida (Efectivo/Tarjeta). */
  private rotuloFormaPagamento(): string {
    const chaves: Record<FormaDePagamentoPedido, string> = {
      DINHEIRO: 'criar_pedido.forma_pagamento_efectivo',
      TARJETA: 'criar_pedido.forma_pagamento_tarjeta',
      QR_CODE: 'criar_pedido.forma_pagamento_qrcode',
      TRANSFERENCIA: 'criar_pedido.forma_pagamento_transferencia',
    };
    return this.translate.instant(chaves[this.formaPagamento]);
  }

  /**
   * Observação geral enviada no POST /pedidos. Em delivery, anexa a forma de
   * pagamento escolhida ao texto — paridade com o mobile e o cardápio web, já que
   * o Pedido não tem campo estruturado para "Tarjeta". Formato:
   * "<observacao> | Forma de pago: <rótulo>" (sem separador quando vazia).
   */
  private montarObservacaoDoPedido(): string {
    const base = (this.observacao || '').trim();
    if (!this.ehDelivery || !this.exibirFormasPagamento) return base;
    const trecho = `${this.translate.instant('criar_pedido.forma_pagamento_observacao')}: ${this.rotuloFormaPagamento()}`;
    return base ? `${base} | ${trecho}` : trecho;
  }

  criarPedido(): void {
    if (!this.podeCriar) return;
    if (!this.validarAntesDeCriar()) return;

    // O backend tambem recusa, mas avisar aqui evita o round-trip e diz o que fazer.
    if (!this.pagamentoQrCodePronto) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('criar_pedido.qrcode_aguardando'),
      });
      return;
    }

    this.salvando = true;
    // Hora LOCAL (não UTC): paridade com o mobile e com a tela de Vendas. Em
    // Asunción (UTC-3/-4), toISOString adiantaria a emissão em 3-4h.
    const agora = toLocalISOString(new Date());
    const ehDelivery = this.ehDelivery;
    const valorFrete = ehDelivery ? (Number(this.valorFrete) || 0) : 0;
    const totalItens = this.total;
    const totalGeral = totalItens + valorFrete;

    // Mesa/comanda só se aplicam ao consumo local.
    const ehLocal = this.modalidade === 'LOCAL';
    const numeroMesa = ehLocal ? this.parseInteiroOuNulo(this.mesa) : null;
    const numeroComanda = ehLocal ? this.parseInteiroOuNulo(this.comanda) : null;

    const itens = this.carrinho.map((l) => {
      const item: Record<string, unknown> = {
        itemId: l.itemId,
        quantidade: l.quantidade,
        valorUnitario: l.valorUnitario,
        valorTotal: this.subtotalLinha(l),
        saldo: l.quantidade,
        quantidadeCancelada: 0,
        quantidadeFaturada: 0,
      };

      // Observação por item: junta a observação manual digitada com a da
      // personalização (só envia quando houver algo). Paridade com o mobile.
      const observacao = this.observacaoCompleta(l);
      if (observacao) {
        item['observacao'] = observacao;
      }

      // Personalização: multiplicar quantidade/preço total pela quantidade da
      // linha, igual ao PDV (variante = qtd da linha; modificador = base × qtd).
      if (l.variantesDePreco?.length) {
        item['variantesDePreco'] = l.variantesDePreco.map((v) => ({
          ...v,
          quantidade: l.quantidade,
          precoTotal: (v.precoDeVenda || 0) * l.quantidade,
        }));
      }
      if (l.modificadores?.length) {
        item['modificadores'] = l.modificadores.map((m) => {
          const qtdBase = m.quantidade ?? 1;
          const qtdTotal = qtdBase * l.quantidade;
          return {
            ...m,
            quantidade: qtdTotal,
            precoTotal: (m.precoDeVenda || 0) * qtdTotal,
          };
        });
      }

      return item;
    });

    const dto: Record<string, unknown> = {
      emissao: agora,
      pessoaId: this.clienteSelecionado!.id,
      nomeCliente: this.clienteSelecionado!.nome,
      observacao: this.montarObservacaoDoPedido(),
      // Campo estruturado (o texto na observação continua por compatibilidade
      // com os pedidos antigos, que a listagem ainda lê por regex).
      // Sem "perguntar a forma de pagamento", o pedido vai sem forma.
      formaDePagamento: this.exibirFormasPagamento ? this.formaPagamento : null,
      // Sem conta: o único pagamento que fatura na criação é o QR, e a conta dele vem de Dados
      // de recebimento. As demais formas são faturadas no PDV, que escolhe a conta lá.
      // A cobranca por QR ja foi paga; o backend so amarra ao pedido e fatura.
      intencaoDeRecebimentoId: this.cobrancaQrCode?.intencaoId ?? null,
      itens,
      valorTotal: totalGeral,
      valorTotalEmGuarani: totalGeral,
      valorTotalEmServicos: 0,
      valorTotalEmMercadorias: totalItens,
      moeda: this.moeda,
      // Pedido nasce ABERTO, mesmo o pago por QR (que já sai com item faturado): pagar não é
      // produzir — quem manda para a cozinha é o operador, no "enviar para produção".
      estadoDePedido: EstadoDePedido.ABERTO,
      tipoDePedido: 'COMANDA',
      // Cada modalidade tem o seu tipo de entrega no backend desde a V1.113: antes local e
      // retirada colapsavam em RETIRADA_NO_LOCAL e so davam para separar pela mesa preenchida.
      tipoEntrega: this.tipoEntregaDaModalidade(),
      // Mesa/comanda são opcionais (null quando não informados).
      numeroMesa,
      numeroComanda,
    };

    // Dados de envio apenas quando delivery.
    if (ehDelivery) {
      dto['enderecoDeEntrega'] = this.montarEnderecoDeEntrega();
      dto['referenciaDeEntrega'] = this.referenciaEntrega.trim();
      dto['latitudeEntrega'] = this.latitudeEntrega;
      dto['longitudeEntrega'] = this.longitudeEntrega;
      dto['valorFrete'] = valorFrete;
    }

    this.pedidoService.post('/pedidos', dto).subscribe({
      next: (resposta: any) => {
        this.salvando = false;

        this.pararPollingQrCode();
        this.cobrancaQrCode = null;
        this.qrCodeConfirmado = false;
        this.mostrarConfirmacao = false;
        this.destruirMapa();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant(
            resposta?.faturado ? 'criar_pedido.criado_e_faturado' : 'criar_pedido.criado_sucesso'
          ),
        });
        this.resetar();
        // Recarrega o grid: o saldo dos itens vendidos foi reduzido no backend.
        this.carregarProdutos();
      },
      error: (err) => {
        this.salvando = false;
        this.messageService.add({
          severity: 'error',
          summary: err?.error?.message || this.translate.instant('criar_pedido.erro_criar'),
        });
      },
    });
  }

  /**
   * Converte o valor de mesa/comanda em inteiro (ou null se vazio/ inválido).
   * Aceita string OU number: os inputs `type="number"` usam o NumberValueAccessor
   * do Angular, que grava um number no ngModel — por isso o `String(...)` antes
   * do trim (chamar `.trim()` direto num number lançava TypeError e abortava o
   * POST, deixando o botão travado em "salvando").
   */
  private parseInteiroOuNulo(valor: unknown): number | null {
    const n = parseInt(String(valor ?? '').trim(), 10);
    return isNaN(n) ? null : n;
  }

  private resetar(): void {
    this.carrinho = [];
    this.observacao = '';
    this.modalidade = 'LOCAL';
    this.formaPagamento = this.formaPagamentoOptions[0]?.value ?? 'DINHEIRO';
    this.mesa = '';
    this.comanda = '';
    this.enderecoEntregaRua = '';
    this.numeroEntrega = '';
    this.complementoEntrega = '';
    this.referenciaEntrega = '';
    this.cidadeEntregaSelecionada = undefined;
    this.latitudeEntrega = null;
    this.longitudeEntrega = null;
    this.valorFrete = 0;
    // Mantém o cliente selecionado para agilizar pedidos consecutivos do mesmo balcão.
  }
}
